import React, { useState, useEffect, useCallback } from 'react';
import { collection, addDoc, getDocs, query, orderBy, deleteDoc, doc, updateDoc, getDoc, where, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
    Briefcase, Plus, MapPin, DollarSign, Clock, Trash2, Search,
    Edit2, X, Building2, Phone, Users, ChevronRight
} from 'lucide-react';

function JobBoard({ user }) {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [userData, setUserData] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [editingJob, setEditingJob] = useState(null);
    const [selectedJob, setSelectedJob] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filter, setFilter] = useState('all');
    const [employmentFilter, setEmploymentFilter] = useState('all');
    const [regionFilter, setRegionFilter] = useState('all');
    const [businessFilter, setBusinessFilter] = useState('all');

    const [formData, setFormData] = useState({
        title: '', position: '치과위생사', location: '', salary: '',
        employmentType: '정규직', description: '', contactPhone: ''
    });

    const loadUserData = useCallback(async () => {
        if (!user?.uid) return;
        try {
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            if (userDoc.exists()) {
                const data = userDoc.data();
                setUserData(data);
                if (!editingJob) {
                    setFormData(prev => ({
                        ...prev,
                        contactPhone: '',
                        location: ''
                    }));
                }
            }
        } catch (error) {
            console.error('사용자 정보 로드 실패:', error);
        }
    }, [user, editingJob]);

    const loadJobs = useCallback(async () => {
        try {
            const jobsRef = collection(db, 'jobPostings');
            const q = query(jobsRef, where('status', '==', 'active'), orderBy('createdAt', 'desc'));
            const querySnapshot = await getDocs(q);
            const jobsList = [];
            querySnapshot.forEach((docSnap) => {
                jobsList.push({ id: docSnap.id, ...docSnap.data() });
            });
            setJobs(jobsList);
        } catch (error) {
            console.error('구인 공고 로드 실패:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (user) {
            loadUserData();
            loadJobs();
        }
    }, [user, loadUserData, loadJobs]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (editingJob) {
            await updateJobPosting();
            return;
        }
        try {
            const jobData = {
                ...formData,
                userId: user.uid,
                companyName: userData?.businessName || userData?.name || user.email,
                businessType: userData?.businessType || 'dental',
                status: 'active',
                views: 0,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            };
            await addDoc(collection(db, 'jobPostings'), jobData);
            alert('구인공고가 등록되었습니다!');
            resetForm();
            loadJobs();
        } catch (error) {
            console.error('공고 등록 실패:', error);
            alert('공고 등록에 실패했습니다.');
        }
    };

    const updateJobPosting = async () => {
        try {
            const jobData = {
                ...formData,
                userId: user.uid,
                companyName: userData?.businessName || '',
                businessType: userData?.businessType || '',
                updatedAt: serverTimestamp()
            };
            await updateDoc(doc(db, 'jobPostings', editingJob.id), jobData);
            alert('공고가 수정되었습니다!');
            resetForm();
            loadJobs();
        } catch (error) {
            console.error('공고 수정 실패:', error);
            alert('공고 수정에 실패했습니다.');
        }
    };

    const startEdit = (job) => {
        setEditingJob(job);
        setFormData({
            title: job.title || '', position: job.position || '치과위생사',
            location: job.location || '', salary: job.salary || '',
            employmentType: job.employmentType || '정규직',
            description: job.description || '', contactPhone: job.contactPhone || ''
        });
        setShowForm(true);
        setSelectedJob(null);
    };

    const handleDelete = async (jobId) => {
        if (!window.confirm('정말 삭제하시겠습니까?')) return;
        try {
            await deleteDoc(doc(db, 'jobPostings', jobId));
            alert('삭제되었습니다.');
            setSelectedJob(null);
            loadJobs();
        } catch (error) {
            console.error('삭제 실패:', error);
            alert('삭제에 실패했습니다.');
        }
    };

    const handleClose = async (jobId) => {
        if (!window.confirm('이 공고를 마감하시겠습니까?')) return;
        try {
            await updateDoc(doc(db, 'jobPostings', jobId), {
                status: 'closed',
                updatedAt: serverTimestamp()
            });
            alert('공고가 마감되었습니다.');
            setSelectedJob(null);
            loadJobs();
        } catch (error) {
            console.error('마감 실패:', error);
            alert('마감에 실패했습니다.');
        }
    };

    const resetForm = () => {
        setFormData({
            title: '', position: '치과위생사', location: '',
            salary: '', employmentType: '정규직', description: '',
            contactPhone: ''
        });
        setShowForm(false);
        setEditingJob(null);
    };

    const regions = ['all', '서울', '경기', '인천', '부산', '대구', '광주', '대전', '울산', '세종', '강원', '충북', '충남', '전북', '전남', '경북', '경남', '제주'];

    const filteredJobs = jobs.filter(job => {
        const matchesSearch = job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            job.companyName?.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesPosition = filter === 'all' || job.position === filter;
        const matchesEmployment = employmentFilter === 'all' || job.employmentType === employmentFilter;
        const matchesRegion = regionFilter === 'all' || (job.location || '').includes(regionFilter);
        const matchesBusiness = businessFilter === 'all' ||
            (businessFilter === '치과' ? job.businessType === 'dental' : job.businessType === 'lab');
        return matchesSearch && matchesPosition && matchesEmployment && matchesRegion && matchesBusiness;
    });

    const canPost = () => {
        const bt = userData?.companyBusinessType || userData?.businessType;
        return bt === 'dental' || bt === 'lab';
    };
    const canEdit = (job) => job.userId === user?.uid;

    if (loading) {
        return (
            <div style={styles.loading}>
                <div style={styles.spinner}></div>
                <p style={{ marginTop: '20px', color: '#64748b' }}>로딩 중...</p>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <div style={styles.titleSection}>
                        <Briefcase size={32} color="#6366f1" />
                        <div>
                            <h1 style={styles.title}>구인공고</h1>
                            <p style={styles.subtitle}>치과/기공소의 구인공고를 확인하고 인재를 채용하세요</p>
                        </div>
                    </div>
                </div>
                {canPost() && (
                    <button style={styles.createButton} onClick={() => { setEditingJob(null); setShowForm(true); }}>
                        <Plus size={20} />구인공고 등록
                    </button>
                )}
            </div>

            <div style={styles.searchSection}>
                <div style={styles.searchBox}>
                    <Search size={20} style={styles.searchIcon} />
                    <input type="text" placeholder="공고 제목, 업체명으로 검색..." value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)} style={styles.searchInput} />
                </div>
            </div>

            <div style={styles.filterSection}>
                <div style={styles.filterRow}>
                    <span style={styles.filterLabel}>직종</span>
                    <div style={styles.filterButtons}>
                        {['all', '치과위생사', '치과기공사', '치과조무사', '기타'].map(f => (
                            <button key={f} style={filter === f ? styles.filterButtonActive : styles.filterButton}
                                onClick={() => setFilter(f)}>
                                {f === 'all' ? '전체' : f}
                            </button>
                        ))}
                    </div>
                </div>
                <div style={styles.filterRow}>
                    <span style={styles.filterLabel}>고용형태</span>
                    <div style={styles.filterButtons}>
                        {['all', '정규직', '파트타임', '계약직'].map(f => (
                            <button key={f} style={employmentFilter === f ? styles.filterButtonActive : styles.filterButton}
                                onClick={() => setEmploymentFilter(f)}>
                                {f === 'all' ? '전체' : f}
                            </button>
                        ))}
                    </div>
                </div>
                <div style={styles.filterRow}>
                    <span style={styles.filterLabel}>지역</span>
                    <div style={styles.filterButtons}>
                        {regions.map(f => (
                            <button key={f} style={regionFilter === f ? styles.filterButtonActive : styles.filterButton}
                                onClick={() => setRegionFilter(f)}>
                                {f === 'all' ? '전체' : f}
                            </button>
                        ))}
                    </div>
                </div>
                <div style={styles.filterRow}>
                    <span style={styles.filterLabel}>업종</span>
                    <div style={styles.filterButtons}>
                        {['all', '치과', '기공소'].map(f => (
                            <button key={f} style={businessFilter === f ? styles.filterButtonActive : styles.filterButton}
                                onClick={() => setBusinessFilter(f)}>
                                {f === 'all' ? '전체' : f}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {filteredJobs.length === 0 ? (
                <div style={styles.emptyState}>
                    <div style={styles.emptyIcon}><Briefcase size={64} /></div>
                    <h3 style={styles.emptyTitle}>등록된 구인공고가 없습니다</h3>
                    <p style={styles.emptyText}>{canPost() && '첫 번째 구인공고를 등록해보세요!'}</p>
                </div>
            ) : (
                <div style={styles.jobGrid}>
                    {filteredJobs.map((job) => (
                        <div key={job.id} style={styles.jobCard} onClick={() => setSelectedJob(job)}>
                            <div style={styles.jobCardHeader}>
                                <div style={styles.companyInfo}>
                                    <Building2 size={18} />
                                    <span style={styles.companyName}>{job.companyName || '업체명 미등록'}</span>
                                    <span style={styles.businessTypeBadge}>{job.businessType === 'dental' ? '치과' : '기공소'}</span>
                                </div>
                                {canEdit(job) && <div style={styles.editBadge}>내 공고</div>}
                            </div>
                            <h3 style={styles.jobTitle}>{job.title}</h3>
                            <div style={styles.jobInfo}>
                                <div style={styles.infoRow}><Users size={16} /><span>{job.position}</span></div>
                                <div style={styles.infoRow}><MapPin size={16} /><span>{job.location || '위치 미등록'}</span></div>
                                <div style={styles.infoRow}><DollarSign size={16} /><span>{job.salary || '급여 협의'}</span></div>
                                <div style={styles.infoRow}><Clock size={16} /><span>{job.employmentType}</span></div>
                            </div>
                            <div style={styles.jobFooter}>
                                <span style={styles.viewsBadge}>조회 {job.views || 0}</span>
                            </div>
                            <div style={styles.arrowIcon}><ChevronRight size={20} /></div>
                        </div>
                    ))}
                </div>
            )}

            {/* 등록/수정 폼 모달 */}
            {showForm && (
                <div style={styles.modalOverlay} onClick={() => setShowForm(false)}>
                    <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h2 style={styles.modalTitle}>{editingJob ? '구인공고 수정' : '구인공고 등록'}</h2>
                            <button style={styles.modalCloseButton} onClick={() => { setShowForm(false); resetForm(); }}>
                                <X size={24} />
                            </button>
                        </div>
                        <div style={styles.modalBody}>
                            <form onSubmit={handleSubmit} style={styles.form}>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>공고 제목 *</label>
                                    <input type="text" value={formData.title} maxLength={40}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        required style={styles.input} placeholder="예: 치과위생사 모집" />
                                </div>
                                <div style={styles.formRow}>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>모집 직종 *</label>
                                        <select value={formData.position} onChange={(e) => setFormData({ ...formData, position: e.target.value })} style={styles.select}>
                                            <option value="치과위생사">치과위생사</option>
                                            <option value="치과기공사">치과기공사</option>
                                            <option value="치과조무사">치과조무사</option>
                                            <option value="기타">기타</option>
                                        </select>
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>고용형태 *</label>
                                        <select value={formData.employmentType} onChange={(e) => setFormData({ ...formData, employmentType: e.target.value })} style={styles.select}>
                                            <option value="정규직">정규직</option>
                                            <option value="파트타임">파트타임</option>
                                            <option value="계약직">계약직</option>
                                        </select>
                                    </div>
                                </div>
                                <div style={styles.formRow}>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>급여 *</label>
                                        <input type="text" value={formData.salary}
                                            onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                                            required style={styles.input} placeholder="예: 월 250만원, 협의" />
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>근무지역 *</label>
                                        <input type="text" value={formData.location}
                                            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                            required style={styles.input} placeholder="예: 서울 강남구" />
                                    </div>
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}>상세 설명</label>
                                    <textarea value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        style={styles.textarea} rows={6} placeholder="업무 내용, 자격 요건 등을 자세히 적어주세요..." />
                                </div>
                                <div style={styles.formGroup}>
                                    <label style={styles.label}><Phone size={16} />연락처 *</label>
                                    <input type="tel" value={formData.contactPhone}
                                        onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                                        required style={styles.input} placeholder="010-0000-0000" />
                                </div>
                                <div style={styles.modalActions}>
                                    <button type="button" onClick={() => { setShowForm(false); resetForm(); }} style={styles.cancelButton}>취소</button>
                                    <button type="submit" style={styles.submitButton}>{editingJob ? '수정하기' : '등록하기'}</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* 상세 보기 모달 */}
            {selectedJob && (
                <div style={styles.modalOverlay} onClick={() => setSelectedJob(null)}>
                    <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.modalHeader}>
                            <h2 style={styles.modalTitle}>구인공고 상세</h2>
                            <button style={styles.modalCloseButton} onClick={() => setSelectedJob(null)}><X size={24} /></button>
                        </div>
                        <div style={styles.modalBody}>
                            <div style={styles.modalCompanyInfo}>
                                <div style={styles.companyInfoLeft}>
                                    <Building2 size={20} />
                                    <span style={styles.modalCompanyName}>{selectedJob.companyName || '업체명 미등록'}</span>
                                    <span style={styles.businessTypeBadgeLarge}>{selectedJob.businessType === 'dental' ? '치과' : '기공소'}</span>
                                </div>
                                {canEdit(selectedJob) && (
                                    <div style={styles.modalActionsDiv}>
                                        <button style={styles.modalEditButton} onClick={() => startEdit(selectedJob)}>
                                            <Edit2 size={16} />수정
                                        </button>
                                        <button style={styles.modalCloseJobButton} onClick={() => handleClose(selectedJob.id)}>
                                            <Clock size={16} />마감
                                        </button>
                                        <button style={styles.modalDeleteButton} onClick={() => handleDelete(selectedJob.id)}>
                                            <Trash2 size={16} />삭제
                                        </button>
                                    </div>
                                )}
                            </div>
                            <h3 style={styles.modalJobTitle}>{selectedJob.title}</h3>
                            <div style={styles.infoGrid}>
                                <div style={styles.infoGridItem}>
                                    <span style={styles.infoLabel}>모집 직종</span>
                                    <span style={styles.infoValue}>{selectedJob.position}</span>
                                </div>
                                <div style={styles.infoGridItem}>
                                    <span style={styles.infoLabel}>근무지역</span>
                                    <span style={styles.infoValue}>{selectedJob.location || '미등록'}</span>
                                </div>
                                <div style={styles.infoGridItem}>
                                    <span style={styles.infoLabel}>급여</span>
                                    <span style={styles.infoValue}>{selectedJob.salary || '협의'}</span>
                                </div>
                                <div style={styles.infoGridItem}>
                                    <span style={styles.infoLabel}>고용형태</span>
                                    <span style={styles.infoValue}>{selectedJob.employmentType}</span>
                                </div>
                                <div style={styles.infoGridItem}>
                                    <span style={styles.infoLabel}>조회수</span>
                                    <span style={styles.infoValue}>{selectedJob.views || 0}회</span>
                                </div>
                            </div>
                            {selectedJob.description && (
                                <div style={styles.detailSection}>
                                    <h4 style={styles.detailTitle}>상세 설명</h4>
                                    <p style={styles.detailContent}>{selectedJob.description}</p>
                                </div>
                            )}
                            {selectedJob.contactPhone && (
                                <div style={styles.contactSection}>
                                    <h4 style={styles.contactSectionTitle}>연락처</h4>
                                    <div style={styles.contactItem}>
                                        <Phone size={20} color="#0369a1" />
                                        <div>
                                            <div style={styles.contactLabel}>전화번호</div>
                                            <a href={`tel:${selectedJob.contactPhone}`} style={styles.contactValue}>{selectedJob.contactPhone}</a>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const styles = {
    container: { maxWidth: '1400px', margin: '0 auto', padding: '32px', backgroundColor: '#f8fafc', minHeight: '100vh' },
    loading: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px' },
    spinner: { width: '40px', height: '40px', border: '3px solid #e2e8f0', borderTop: '3px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px', gap: '20px', flexWrap: 'wrap' },
    headerLeft: { flex: 1 },
    titleSection: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' },
    title: { margin: 0, fontSize: '32px', fontWeight: '700', color: '#0f172a' },
    subtitle: { margin: '4px 0 0 0', fontSize: '15px', color: '#64748b' },
    createButton: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', backgroundColor: '#6366f1', color: 'white', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)' },
    searchSection: { display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' },
    filterSection: { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '32px', padding: '16px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0' },
    filterRow: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
    filterLabel: { fontSize: '13px', fontWeight: '700', color: '#475569', minWidth: '56px', flexShrink: 0 },
    searchBox: { flex: 1, position: 'relative', minWidth: '300px' },
    searchIcon: { position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' },
    searchInput: { width: '100%', padding: '12px 16px 12px 48px', border: '2px solid #e2e8f0', borderRadius: '12px', fontSize: '15px', outline: 'none', transition: 'all 0.2s' },
    filterButtons: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
    filterButton: { padding: '6px 14px', backgroundColor: 'white', border: '1.5px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', fontWeight: '600', color: '#64748b', cursor: 'pointer', transition: 'all 0.2s' },
    filterButtonActive: { padding: '6px 14px', backgroundColor: '#6366f1', border: '1.5px solid #6366f1', borderRadius: '8px', fontSize: '13px', fontWeight: '600', color: 'white', cursor: 'pointer', transition: 'all 0.2s' },
    jobGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '24px' },
    jobCard: { position: 'relative', backgroundColor: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)', border: '2px solid #f1f5f9', cursor: 'pointer', transition: 'all 0.3s' },
    jobCardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', gap: '12px' },
    companyInfo: { display: 'flex', alignItems: 'center', gap: '8px', flex: 1 },
    companyName: { fontSize: '14px', fontWeight: '600', color: '#475569' },
    businessTypeBadge: { padding: '3px 8px', fontSize: '11px', fontWeight: '600', backgroundColor: '#f1f5f9', color: '#475569', borderRadius: '6px' },
    editBadge: { padding: '4px 10px', fontSize: '11px', fontWeight: '600', backgroundColor: '#dbeafe', color: '#1e40af', borderRadius: '6px' },
    jobTitle: { margin: '0 0 16px 0', fontSize: '20px', fontWeight: '700', color: '#1e293b', lineHeight: '1.4' },
    jobInfo: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' },
    infoRow: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#64748b' },
    jobFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' },
    viewsBadge: { fontSize: '13px', fontWeight: '600', color: '#94a3b8' },
    arrowIcon: { position: 'absolute', top: '50%', right: '16px', transform: 'translateY(-50%)', color: '#cbd5e1', opacity: 0, transition: 'all 0.3s' },
    emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', backgroundColor: 'white', borderRadius: '16px', border: '2px dashed #e2e8f0' },
    emptyIcon: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100px', height: '100px', backgroundColor: '#f8fafc', borderRadius: '50%', marginBottom: '24px', color: '#cbd5e1' },
    emptyTitle: { margin: '0 0 8px 0', fontSize: '20px', fontWeight: '700', color: '#1e293b' },
    emptyText: { margin: 0, fontSize: '14px', color: '#64748b' },
    modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    modal: { backgroundColor: 'white', borderRadius: '20px', boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)', maxWidth: '800px', width: '90%', maxHeight: '90vh', overflow: 'hidden', zIndex: 1000, display: 'flex', flexDirection: 'column' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px 32px', borderBottom: '2px solid #f1f5f9', backgroundColor: '#fafbfc' },
    modalTitle: { margin: 0, fontSize: '22px', fontWeight: '700', color: '#1e293b' },
    modalCloseButton: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '40px', height: '40px', backgroundColor: 'transparent', border: 'none', borderRadius: '10px', cursor: 'pointer', color: '#64748b', transition: 'all 0.2s' },
    modalBody: { padding: '32px', overflowY: 'auto', flex: 1 },
    modalCompanyInfo: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '16px' },
    companyInfoLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
    modalCompanyName: { fontSize: '16px', fontWeight: '600', color: '#475569' },
    businessTypeBadgeLarge: { padding: '5px 12px', fontSize: '12px', fontWeight: '600', backgroundColor: '#f1f5f9', color: '#475569', borderRadius: '12px' },
    modalActionsDiv: { display: 'flex', gap: '8px' },
    modalEditButton: { display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', backgroundColor: 'transparent', border: '2px solid #6366f1', borderRadius: '8px', color: '#6366f1', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' },
    modalCloseJobButton: { display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', backgroundColor: 'transparent', border: '2px solid #f59e0b', borderRadius: '8px', color: '#f59e0b', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' },
    modalDeleteButton: { display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', backgroundColor: 'transparent', border: '2px solid #ef4444', borderRadius: '8px', color: '#ef4444', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' },
    modalJobTitle: { margin: '0 0 24px 0', fontSize: '26px', fontWeight: '700', color: '#1e293b', lineHeight: '1.4' },
    infoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '32px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' },
    infoGridItem: { display: 'flex', flexDirection: 'column', gap: '4px' },
    infoLabel: { fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' },
    infoValue: { fontSize: '15px', fontWeight: '600', color: '#1e293b' },
    detailSection: { marginBottom: '24px', padding: '20px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' },
    detailTitle: { margin: '0 0 12px 0', fontSize: '16px', fontWeight: '700', color: '#475569' },
    detailContent: { margin: 0, fontSize: '15px', color: '#64748b', lineHeight: '1.7', whiteSpace: 'pre-wrap' },
    contactSection: { padding: '24px', backgroundColor: '#f0f9ff', borderRadius: '12px', border: '2px solid #bae6fd' },
    contactSectionTitle: { margin: '0 0 16px 0', fontSize: '18px', fontWeight: '700', color: '#0369a1' },
    contactItem: { display: 'flex', alignItems: 'flex-start', gap: '12px' },
    contactLabel: { fontSize: '12px', fontWeight: '600', color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' },
    contactValue: { fontSize: '15px', fontWeight: '600', color: '#0c4a6e', textDecoration: 'none' },
    form: { display: 'flex', flexDirection: 'column', gap: '20px' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
    formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
    label: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: '600', color: '#475569' },
    input: { padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'all 0.2s' },
    textarea: { padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', resize: 'vertical', fontFamily: 'inherit', minHeight: '80px', transition: 'all 0.2s' },
    select: { padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', backgroundColor: 'white', cursor: 'pointer' },
    modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '8px' },
    cancelButton: { padding: '10px 24px', backgroundColor: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' },
    submitButton: { padding: '10px 24px', background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }
};

const styleSheet = document.createElement('style');
styleSheet.textContent = `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`;
document.head.appendChild(styleSheet);

export default JobBoard;
